import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastOptions, ToastConfig } from './toast-types';
import { I18nService } from '../i18n/i18n.service';
import { GenericUploadErrorEvent } from 'app/shared/components/generic-upload';

/**
 * Generic Toast Service
 * Provides a consistent API for displaying toast notifications
 * Internally uses ngx-toastr for actual toast implementation
 */
@Injectable({
  providedIn: 'root'
})
export class ToastService {
  uniqueCounter: number = 0;

  constructor(
    private toastr: ToastrService,
    private i18n: I18nService,
    private config?: ToastConfig
  ) {
    if (!config) {
      this.config = {
        showClose: true,
        timeout: 5000
      };
    }
  }

  /**
   * Create Toast of a default type
   */
  default(options: ToastOptions | string | number): void {
    this.showToast('default', options);
  }

  /**
   * Create Toast of info type
   * @param options Individual toast config overrides
   */
  info(options: ToastOptions | string | number): void {
    this.showToast('info', options);
  }

  /**
   * Create Toast of success type
   * @param options Individual toast config overrides
   */
  success(options: ToastOptions | string | number): void {
    this.showToast('success', options);
  }

  /**
   * Create Toast of wait type
   * @param options Individual toast config overrides
   */
  wait(options: ToastOptions | string | number): void {
    this.showToast('info', options); // Map wait to info since ngx-toastr doesn't have wait
  }

  /**
   * Create Toast of error type
   * @param options Individual toast config overrides
   */
  error(options: ToastOptions | string | number): void {
    this.showToast('error', options);
  }

  /**
   * Create Toast of warning type
   * @param options Individual toast config overrides
   */
  warning(options: ToastOptions | string | number): void {
    this.showToast('warning', options);
  }

  // ===== ToastService Methods =====
  
  public getErrorMessage(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        return error.error.reason === 'ValidationError' ? 
            error.error.data.message : 
            error.error.description;

      case 403:
        if (error.error) {
          if (error.error.reason === 'LockedByOtherUser') {
            const currentlyLockedBy: string[] = error.error.data;
            return this.i18n
                .getLocalizedString('validation.messages.attributeLockedBy')
                .replace('{{user}}', currentlyLockedBy[0]);
          } else if (error.error.reason === 'Authorization') {
            return this.i18n.getLocalizedString('validation.messages.notAuthorized');
          }
          if (error.error.description) {
            return error.error.description;
          }
        }
        return error.message;

      case 404:
        return this.i18n.getLocalizedString('validation.messages.notFound');

      case 409:
        return this.i18n.getLocalizedString('validation.messages.alreadyOpen');

      case 500:
        return error.error.error;

      default:
        return error.message;
    }
  }

  public getErrorMessageTitle(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        return this.i18n.getLocalizedString(`validation.titles.${error.error.reason}`);

      case 403:
        if (error.error) {
          if (error.error.reason === 'LockedByOtherUser') {
            return this.i18n.getLocalizedString('validation.titles.locked');
          }
        }
        return this.i18n.getLocalizedString('validation.titles.notAuthorized');       

      case 404:
        return this.i18n.getLocalizedString('validation.titles.notFound');

      case 409:
        return this.i18n.getLocalizedString('validation.titles.alreadyOpen');
        
      case 500:
        return this.i18n.getLocalizedString('validation.titles.error');

      default:
        return error.statusText
    }
  }

  /**
   * Handle HTTP error responses and show appropriate toast messages
   * @param error HTTP error response
   */
  public toastError(error: HttpErrorResponse): void {
    if (error.status === 400 || error.status === 403) {
        this.warning({
          title: this.getErrorMessageTitle(error),
          msg: this.getErrorMessage(error),
          showClose: true,
          timeout: 5000,
        });
    } else {
      this.error({
        title: this.getErrorMessageTitle(error),
        msg: this.getErrorMessage(error),
        showClose: true,
        timeout: 0,
      });
    }
  }

  /**
   * Show localized warning toast
   * @param titleKey Localization key for title
   * @param msgKey Localization key for message
   */
  public toastWarning(titleKey: string, msgKey: string): void {
    this.toastWarningFormatted(titleKey, msgKey, {});
  }

  /**
   * Show formatted localized warning toast
   * @param titleKey Localization key for title
   * @param msgKey Localization key for message
   * @param data Data for string interpolation
   */
  public toastWarningFormatted(titleKey: string, msgKey: string, data: any): void {
    const title = this.i18n.getLocalizedString(titleKey);
    let msg = this.i18n.getLocalizedString(msgKey);
    for (const key of Object.keys(data)) {
      msg = msg.replace(`{{${key}}}`, data[key]);
    }
    this.warning({
      title: title,
      msg: msg,
      showClose: true,
      timeout: 0,
    });
  }

  /**
   * Show formatted localized error toast
   * @param titleKey Localization key for title
   * @param msgKey Localization key for message
   * @param data Data for string interpolation
   */
  public toastErrorFormatted(titleKey: string, msgKey: string, data: any): void {
    const title = this.i18n.getLocalizedString(titleKey);
    let msg = this.i18n.getLocalizedString(msgKey);
    for (const key of Object.keys(data)) {
      msg = msg.replace(`{{${key}}}`, data[key]);
    }
    this.error({
      title: title,
      msg: msg,
      showClose: true,
      timeout: 0,
    });
  }

  /**
   * Show formatted localized success toast
   * @param titleKey Localization key for title
   * @param msgKey Localization key for message
   * @param data Data for string interpolation
   */
  public toastSuccessFormatted(titleKey: string, msgKey: string, data: any): void {
    const title = this.i18n.getLocalizedString(titleKey);
    let msg = this.i18n.getLocalizedString(msgKey);
    for (const key of Object.keys(data)) {
      msg = msg.replace(`{{${key}}}`, data[key]);
    }
    this.success({
      title: title,
      msg: msg,
      showClose: true,
      timeout: 5000,
    });
  }

  /**
   * Show success toast with direct title and message
   * @param title Toast title
   * @param successMessage Toast message
   */
  public toastSuccess(title: string, successMessage: string, showClose: boolean = true): void {
    this.success({
      title: title,
      msg: successMessage,
      showClose: showClose,
      timeout: 5000,
    });
  }

  private showToast(type: string, options: ToastOptions | string | number): void {
    let title = '';
    let message = '';
    let toastOptions: any = {};

    if (typeof options === 'string') {
      message = options;
    } else if (typeof options === 'number') {
      message = options.toString();
    } else if (options) {
      title = options.title || '';
      message = options.msg || '';
      
      // Map toast options to ngx-toastr options
      if (options.timeout !== undefined) {
        toastOptions.timeOut = options.timeout;
      } else if (this.config?.timeout) {
        toastOptions.timeOut = this.config.timeout;
      }
      
      if (options.showClose !== undefined) {
        toastOptions.closeButton = options.showClose;
      } else if (this.config?.showClose) {
        toastOptions.closeButton = this.config.showClose;
      }
    }

    // Show toast using ngx-toastr
    switch (type) {
      case 'success':
        this.toastr.success(message, title, toastOptions);
        break;
      case 'error':
        this.toastr.error(message, title, toastOptions);
        break;
      case 'warning':
        this.toastr.warning(message, title, toastOptions);
        break;
      case 'info':
      default:
        this.toastr.info(message, title, toastOptions);
        break;
    }
  }
}

export function toastServiceFactory(toastr: ToastrService, i18n: I18nService, config?: ToastConfig): ToastService {
  return new ToastService(toastr, i18n, config);
}
